import { useState } from 'react';
import { useUsers } from '../hooks/use-users';
import { createUser, updateUser, changePassword } from '../api/users-api';
import { UserList } from '../components/user-list';
import { CreateUserModal } from '../components/create-user-modal';
import { EditUserModal } from '../components/edit-user-modal';
import type { User, CreateUserRequest, UpdateUserRequest } from '@/types/user';

export function UsersPage() {
  const { users, isLoading, error, refetch } = useUsers();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

  const handleCreateUser = async (data: CreateUserRequest) => {
    setIsCreating(true);
    try {
      const response = await createUser(data);
      if (response.success) {
        setIsCreateModalOpen(false);
        refetch();
      } else {
        throw new Error(response.message || 'Failed to create user');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditUser = async (id: number, data: UpdateUserRequest) => {
    setIsUpdating(true);
    try {
      const response = await updateUser(id, data);
      if (response.success) {
        setEditingUser(null);
        refetch();
      } else {
        throw new Error(response.message || 'Failed to update user');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async (id: number, newPassword: string) => {
    await changePassword(id, newPassword);
  };

  const handleToggleStatus = async (user: User) => {
    setUpdatingUserId(user.id);
    try {
      await updateUser(user.id, { isActive: !user.isActive });
      refetch();
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 bg-gray-700 rounded w-32 animate-pulse" />
          <div className="h-10 bg-gray-700 rounded w-28 animate-pulse" />
        </div>
        <div className="h-96 bg-gray-800 rounded animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Users</h1>
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Users</h1>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
        >
          Create User
        </button>
      </div>

      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <UserList
          users={users}
          onEdit={setEditingUser}
          onToggleStatus={handleToggleStatus}
          updatingUserId={updatingUserId}
        />
      </div>

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        isSubmitting={isCreating}
      />

      <EditUserModal
        user={editingUser}
        onClose={() => setEditingUser(null)}
        onSubmit={handleEditUser}
        onChangePassword={handleChangePassword}
        isSubmitting={isUpdating}
      />
    </div>
  );
}
